import React, { useMemo } from "react";
import { motion } from "framer-motion";

// AVA's Cognitive Architecture - a live, animated visual metaphor for the two
// cognitive layers that make her tick, drawn as two lobes joined by a symbiotic
// consolidation stream.
//
// LEFT LOBE - HIPPOCAMPUS (cyan):
//   Fast, plastic, associative memory. Rendered as an off-centre orbital ring of
//   nodes rotating around a bright core, with Hebbian synapse chords that pulse
//   between random node pairs - visualising cells firing together, wiring
//   together in real time.
//
// RIGHT LOBE - NEOCORTEX (violet):
//   Slow, structural, deep-consolidated reasoning. Rendered as concentric cortical
//   sheets with faint radiating spoke fibres, layered like the six laminae of the
//   biological neocortex. The layers breathe slowly, evoking systems consolidation
//   rather than moment-to-moment plasticity.
//
// BRIDGE:
//   A stream of bidirectional particles flowing across the corpus-callosum axis
//   between the lobes - hippocampal engrams being replayed into cortical long-term
//   structure, and cortical priors returning back down for context-conditioned recall.
//
// Purely CSS/SVG + Framer Motion - no images, no external assets.

const HIPPO_NODE_COUNT = 9;
const CORTEX_LAYERS = 5;

function HippocampusLobe() {
  const nodes = useMemo(() => Array.from({ length: HIPPO_NODE_COUNT }, (_, i) => {
    const angle = (i / HIPPO_NODE_COUNT) * Math.PI * 2;
    return { i, x: 50 + Math.cos(angle) * 26, y: 50 + Math.sin(angle) * 26 };
  }), []);
  // Two rotating Hebbian chords - pick a small handful of node pairs and animate
  // a bright edge sweeping between them. This is what "fires together, wires
  // together" looks like in motion.
  const chords = useMemo(() => [
    [nodes[0], nodes[4]],
    [nodes[1], nodes[6]],
    [nodes[2], nodes[7]],
    [nodes[3], nodes[8]],
  ], [nodes]);

  return (
    <div className="relative w-24 h-24 sm:w-28 sm:h-28" data-testid="cog-hippocampus">
      <motion.div
        className="absolute inset-0 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle at 50% 50%, rgba(0,240,255,0.35), rgba(0,240,255,0.08) 45%, transparent 72%)", filter: "blur(3px)" }}
        animate={{ opacity: [0.55, 0.95, 0.55], scale: [1, 1.05, 1] }}
        transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
      />
      <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
        <defs>
          <radialGradient id="hippo-core" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#E0F7FF" stopOpacity="1" />
            <stop offset="60%" stopColor="#00F0FF" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#00F0FF" stopOpacity="0" />
          </radialGradient>
        </defs>
        <motion.g
          animate={{ rotate: 360 }}
          transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
          style={{ transformOrigin: "50px 50px" }}
        >
          {nodes.map((n) => (
            <circle key={n.i} cx={n.x} cy={n.y} r="1.6" fill="#8EEBFF" stroke="#00F0FF" strokeWidth="0.4" />
          ))}
          {chords.map(([a, b], i) => (
            <motion.line
              key={i}
              x1={a.x} y1={a.y} x2={b.x} y2={b.y}
              stroke="#00F0FF"
              strokeWidth="0.5"
              animate={{ opacity: [0.15, 0.85, 0.15], strokeWidth: [0.4, 1.2, 0.4] }}
              transition={{ duration: 1.6 + i * 0.4, repeat: Infinity, ease: "easeInOut", delay: i * 0.35 }}
            />
          ))}
        </motion.g>
        <circle cx="50" cy="50" r="9" fill="url(#hippo-core)" />
        <motion.circle
          cx="50" cy="50" r="3"
          fill="#F0FDFF"
          animate={{ r: [2.5, 3.5, 2.5], opacity: [0.85, 1, 0.85] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
        />
      </svg>
    </div>
  );
}

function NeocortexLobe() {
  return (
    <div className="relative w-24 h-24 sm:w-28 sm:h-28" data-testid="cog-neocortex">
      <motion.div
        className="absolute inset-0 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle at 50% 50%, rgba(167,139,250,0.35), rgba(139,92,246,0.1) 45%, transparent 72%)", filter: "blur(3px)" }}
        animate={{ opacity: [0.5, 0.9, 0.5], scale: [1, 1.04, 1] }}
        transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut" }}
      />
      <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
        <defs>
          <radialGradient id="cortex-core" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#F5F3FF" stopOpacity="1" />
            <stop offset="45%" stopColor="#A78BFA" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0" />
          </radialGradient>
        </defs>
        {/* Concentric cortical laminae - the six-layer neocortical stack, breathing */}
        {Array.from({ length: CORTEX_LAYERS }).map((_, i) => {
          const r = 10 + i * 6;
          return (
            <motion.circle
              key={i}
              cx="50" cy="50" r={r}
              fill="none"
              stroke="#A78BFA"
              strokeOpacity={0.55 - i * 0.08}
              strokeWidth={0.5}
              animate={{ strokeOpacity: [0.55 - i * 0.08, 0.85 - i * 0.09, 0.55 - i * 0.08] }}
              transition={{ duration: 4 + i * 0.4, repeat: Infinity, ease: "easeInOut", delay: i * 0.15 }}
            />
          );
        })}
        {/* Radiating spoke fibres - cortical columns descending through the sheets */}
        {Array.from({ length: 12 }).map((_, i) => {
          const angle = (i / 12) * Math.PI * 2;
          const x1 = 50 + Math.cos(angle) * 12;
          const y1 = 50 + Math.sin(angle) * 12;
          const x2 = 50 + Math.cos(angle) * 34;
          const y2 = 50 + Math.sin(angle) * 34;
          return (
            <motion.line
              key={i}
              x1={x1} y1={y1} x2={x2} y2={y2}
              stroke="#C4B5FD"
              strokeOpacity="0.28"
              strokeWidth="0.4"
              animate={{ strokeOpacity: [0.15, 0.5, 0.15] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: (i % 4) * 0.6 }}
            />
          );
        })}
        <circle cx="50" cy="50" r="14" fill="url(#cortex-core)" />
        <motion.circle
          cx="50" cy="50" r="4"
          fill="#F5F3FF"
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
        />
      </svg>
    </div>
  );
}

// The corpus-callosum stream - animated particles crossing back and forth,
// carrying hippocampal engrams into cortex and cortical priors back down.
function ConsolidationBridge() {
  const particles = useMemo(() => [
    { delay: 0, dir: "ltr", top: 42 },
    { delay: 0.6, dir: "rtl", top: 48 },
    { delay: 1.1, dir: "ltr", top: 55 },
    { delay: 1.5, dir: "rtl", top: 40 },
    { delay: 1.9, dir: "ltr", top: 60 },
  ], []);

  return (
    <div className="relative flex-1 h-24 sm:h-28 mx-1" data-testid="cog-bridge">
      {/* Central axis line - thin, taut, hinting at the corpus callosum */}
      <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-px bg-gradient-to-r from-cyan-400/40 via-white/25 to-violet-400/40" />
      <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-px bg-gradient-to-r from-cyan-400/10 via-white/40 to-violet-400/10 blur-sm" />
      {particles.map((p, i) => (
        <motion.span
          key={i}
          className="absolute w-1 h-1 rounded-full"
          style={{
            top: `${p.top}%`,
            background: p.dir === "ltr" ? "#00F0FF" : "#A78BFA",
            boxShadow: p.dir === "ltr" ? "0 0 8px rgba(0,240,255,0.9)" : "0 0 8px rgba(167,139,250,0.9)",
          }}
          initial={{ left: p.dir === "ltr" ? "0%" : "100%", opacity: 0 }}
          animate={{ left: p.dir === "ltr" ? "100%" : "0%", opacity: [0, 1, 1, 0] }}
          transition={{ duration: 2.6, repeat: Infinity, ease: "linear", delay: p.delay }}
        />
      ))}
      {/* Tiny consolidation label sits over the axis */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <span className="text-[7px] font-mono-data uppercase tracking-[0.3em] text-slate-500 bg-black/60 px-1.5 py-0.5 rounded" data-testid="cog-bridge-label">
          Consolidation
        </span>
      </div>
    </div>
  );
}

export default function CognitiveArchitecture() {
  return (
    <div className="glass-panel rounded-2xl p-4" data-testid="cognitive-architecture-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <motion.span
            className="w-1.5 h-1.5 rounded-full bg-cyan-400"
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 1.8, repeat: Infinity }}
          />
          <span className="text-[10px] font-mono-data uppercase tracking-widest text-slate-400">
            Cognitive Architecture
          </span>
        </div>
        <span className="text-[8px] font-mono-data uppercase tracking-widest text-slate-600">Symbiosis</span>
      </div>

      <div className="flex items-center justify-between gap-1">
        <HippocampusLobe />
        <ConsolidationBridge />
        <NeocortexLobe />
      </div>

      <div className="grid grid-cols-2 gap-3 mt-3">
        <div className="text-left">
          <p className="text-[9px] font-mono-data uppercase tracking-widest text-cyan-300">Hippocampus</p>
          <p className="text-[9px] leading-relaxed text-slate-500 font-mono-data">Fast · plastic · Hebbian associative memory</p>
        </div>
        <div className="text-right">
          <p className="text-[9px] font-mono-data uppercase tracking-widest text-violet-300">Neocortex</p>
          <p className="text-[9px] leading-relaxed text-slate-500 font-mono-data">Slow · structural · deep consolidated reasoning</p>
        </div>
      </div>
    </div>
  );
}
